// GAVION ai-hiring-review (spec docs/superpowers/specs/2026-10-27-ai-hiring-assistant-design.md §4): the AI review as
// « Candidats » shows it. SQL twin: private.recruiting_review_json (92_gavion_ai_hiring_review.sql); the edge's limits:
// supabase/functions/_shared/ai/jobReview.ts. Advice only: nothing here changes a status.
export const AI_REVIEW_STATUSES = [
  "pending",
  "done",
  "failed",
  "unreadable",
] as const;
export type AiReviewStatus = (typeof AI_REVIEW_STATUSES)[number];
export const AI_VERDICTS = ["strong", "consider", "weak"] as const;
export type AiVerdict = (typeof AI_VERDICTS)[number];
export const AI_RATINGS = ["met", "partial", "missing", "unknown"] as const;
export type AiRating = (typeof AI_RATINGS)[number];

/** The list's part of the review (recruiting_applications). */
export type AiReviewSummary = {
  status: AiReviewStatus;
  score: number | null;
  verdict: AiVerdict | null;
};
export type AiCriterion = {
  requirement: string;
  rating: AiRating;
  evidence: string;
};
/** The detail's review (recruiting_application, recruiting_request_review). */
export type AiReview = AiReviewSummary & {
  summary: string | null;
  criteria: AiCriterion[];
  strengths: string[];
  concerns: string[];
  questions: string[];
  error_code: string | null;
  model: string | null;
  reviewed_at: string | null;
  requested_at: string | null;
  /** « Poste modifié depuis l'évaluation »: the posting's text changed after the review. */
  posting_changed: boolean;
};
export const EMPTY_AI_REVIEW: AiReview = Object.freeze({
  status: "pending",
  score: null,
  verdict: null,
  summary: null,
  criteria: [],
  strengths: [],
  concerns: [],
  questions: [],
  error_code: null,
  model: null,
  reviewed_at: null,
  requested_at: null,
  posting_changed: false,
}) as AiReview;
/** « Réévaluer »: once a minute per application (SQL twin: recruiting_request_review, too_soon). */
export const REVIEW_REQUEST_COOLDOWN_MS = 60_000;

export const VERDICT_CLASS: Readonly<Record<AiVerdict, string>> = {
  strong: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
  consider: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100",
  weak: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
};
export const RATING_ICON: Readonly<Record<AiRating, string>> = {
  met: "✓",
  partial: "◐",
  missing: "✗",
  unknown: "?",
};

export const reviewSummary = (r: AiReview | null): AiReviewSummary | null =>
  r && { status: r.status, score: r.score, verdict: r.verdict };

export type ApplicationSort = "date" | "score";
/**
 * « Score IA » sort (spec §4): scored applications first, highest score first (ties keep the list's order); the
 * others after, in the list's order. "date" keeps the list's order (newest first, as the server sends it).
 */
export function sortApplications<
  T extends { ai_review: AiReviewSummary | null },
>(rows: readonly T[], sort: ApplicationSort): T[] {
  if (sort === "date") return [...rows];
  const score = (r: T) =>
    r.ai_review?.status === "done" && r.ai_review.score !== null
      ? r.ai_review.score
      : -1;
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => score(b.r) - score(a.r) || a.i - b.i)
    .map((x) => x.r);
}
